import reservationStatus, {
  castReservation,
  castReservations,
  getReservationsFinishingInFuture,
} from "@/helpers/Reservations";
import httpService from "@/services/HttpService";
import { defineStore } from "pinia";
import { useProfileStore } from "../Profile";
import loggingService from "@/services/LoggingService";
import { convertFromDTO } from "@/helpers/Schedule/index";
import { deviceTimeZone } from "@/helpers/Time";
import { MODE } from "@/types/Schedule";
import { CreatedVia, Reservation } from "@/types/Reservation";
import type { ScheduleDTO } from "@/dto/schedule";
import type { AxiosError } from "axios";
import type { ReservationMutationPayload } from "@/dto/mutation";
import type { ReservationDTO } from "@/dto/reservation";

export const MUTATION_SUPPRESSION_WINDOW_MS = 3_000;

const isReservationDTO = (payload: ReservationMutationPayload): payload is ReservationDTO => {
  if (!payload) {
    return false;
  }

  const candidate = payload as Partial<ReservationDTO> & { id?: unknown };
  const hasValidId = typeof candidate.id === "number" || typeof candidate.id === "string";

  return hasValidId && typeof candidate.startTime === "string" && typeof candidate.endTime === "string";
};

interface InitialState {
  time: Date;
  reservations: Reservation[];
  lastRefreshAt: number | null;
  rateLimitUntil: number | null;
  refreshInProgress: boolean;
  suppressRealtimeRefreshUntil: number | null;
}

// Subsequent refresh requests made within this window are ignored, unless forced, to avoid dogpiling the API when
// multiple components (or a burst of Pusher events) trigger the same update flow.
const REFRESH_THROTTLE_MS = 1_000;
// When the server responds with HTTP 429 we refuse to hit the endpoint again until this cooldown expires, giving the
// backend time to recover from rate limiting before another tablet request is attempted.
const RATE_LIMIT_BACKOFF_MS = 60_000;

let ongoingRefresh: Promise<{ error: AxiosError | null; schedule: ScheduleDTO | null }> | null = null;

type RefreshOptions = {
  force?: boolean;
  source?: string;
};

const parseRefreshOptions = (options?: boolean | RefreshOptions): { force: boolean; source: string } => {
  if (typeof options === "boolean") {
    return { force: options, source: "unspecified" };
  }

  return {
    force: options?.force ?? false,
    source: options?.source ?? "unspecified",
  };
};

export const useScheduleStore = defineStore("schedule", {
  state: (): InitialState => {
    return {
      time: new Date(),
      reservations: [],
      lastRefreshAt: null,
      rateLimitUntil: null,
      refreshInProgress: false,
      suppressRealtimeRefreshUntil: null,
    };
  },
  actions: {
    applyReservationsSnapshot(reservations: Reservation[]) {
      this.reservations = reservations;
      this.time = new Date();
      this.lastRefreshAt = Date.now();
      this.rateLimitUntil = null;
    },
    suppressRealtimeRefresh(source: string, windowMs = MUTATION_SUPPRESSION_WINDOW_MS) {
      this.suppressRealtimeRefreshUntil = Date.now() + windowMs;
      loggingService.debug("Schedule.ts", `🛡️ Suppressing realtime refresh for ${windowMs}ms (source=${source})`);
    },
    shouldSkipRealtimeRefresh(trigger: string): boolean {
      if (!this.suppressRealtimeRefreshUntil) {
        return false;
      }

      const now = Date.now();
      if (now < this.suppressRealtimeRefreshUntil) {
        loggingService.info(
          "Schedule.ts",
          `⏭️ Ignoring realtime refresh (${trigger}) while suppression active (${
            this.suppressRealtimeRefreshUntil - now
          }ms remaining)`
        );
        return true;
      }

      this.suppressRealtimeRefreshUntil = null;
      return false;
    },
    applyReservationUpdate(reservation: Reservation, source: string): boolean {
      let updated = false;
      let reservations = this.reservations.map(item => {
        if (item.id === reservation.id) {
          updated = true;
          return reservation;
        }
        return item;
      });

      if (!updated) {
        reservations = [...reservations, reservation].sort(
          (eventA, eventB) => eventA.startTime.getTime() - eventB.startTime.getTime()
        );
        loggingService.info(
          "Schedule.ts",
          `➕ Added reservation ${reservation.id} from mutation response (source=${source})`
        );
      } else {
        loggingService.info(
          "Schedule.ts",
          `🔁 Updated reservation ${reservation.id} from mutation response (source=${source})`
        );
      }

      this.reservations = reservations;
      return true;
    },
    applyMutationPayload(payload: ReservationMutationPayload | null, source: string): boolean {
      if (!payload) {
        return false;
      }

      let applied = false;

      if (!isReservationDTO(payload)) {
        if (payload.schedule) {
          const { reservations } = convertFromDTO(payload.schedule);
          this.applyReservationsSnapshot(reservations);
          loggingService.info(
            "Schedule.ts",
            `📥 Schedule replaced from mutation response (source=${source}, reservations=${reservations.length})`
          );
          applied = true;
        } else if (payload.reservations) {
          const reservations = castReservations(payload.reservations);
          this.applyReservationsSnapshot(reservations);
          loggingService.info(
            "Schedule.ts",
            `📥 Reservations replaced from mutation response (source=${source}, reservations=${reservations.length})`
          );
          applied = true;
        } else if (payload.reservation) {
          const reservation = castReservation(payload.reservation);
          applied = this.applyReservationUpdate(reservation, source);
        }
      } else {
        const reservation = castReservation(payload);
        applied = this.applyReservationUpdate(reservation, source);
      }

      if (applied) {
        this.suppressRealtimeRefresh(source);
        return true;
      }

      loggingService.warn("Schedule.ts", `⚠️ Mutation response missing reservation data (source=${source})`);
      return false;
    },
    async forceRefreshAfterMutationValidationError(source: string, error: AxiosError | null) {
      if (error?.response?.status !== 422) {
        return;
      }

      loggingService.warn(
        "Schedule.ts",
        `🧼 Mutation ${source} failed with 422, forcing schedule refresh to reconcile local state`
      );

      const refreshResult = await this.refreshTablet({ source: `${source}:validation-error`, force: true });

      if (refreshResult.error) {
        loggingService.error(
          "Schedule.ts",
          `❗ Failed to refresh schedule after mutation validation error (source=${source}): ${refreshResult.error}`
        );
      }
    },
    startTime() {
      setInterval(() => (this.time = new Date()), 1000);
    },
    async refreshTablet(options?: boolean | RefreshOptions) {
      const { force, source } = parseRefreshOptions(options);
      const now = Date.now();

      loggingService.debug(
        "Schedule.ts",
        `📝 Schedule refresh requested (source=${source}, force=${force}, inFlight=${!!ongoingRefresh})`
      );

      if (!force) {
        if (this.rateLimitUntil && now < this.rateLimitUntil) {
          loggingService.warn(
            "Schedule.ts",
            `⏳ Skipping schedule refresh (source=${source}) due to active cooldown (${
              this.rateLimitUntil - now
            }ms remaining)`
          );
          return { error: null, schedule: null };
        }

        if (ongoingRefresh) {
          loggingService.debug(
            "Schedule.ts",
            `🔄 Joining in-flight schedule refresh (source=${source}, elapsed ${now - (this.lastRefreshAt ?? now)}ms)`
          );
          return ongoingRefresh;
        }

        if (this.lastRefreshAt && now - this.lastRefreshAt < REFRESH_THROTTLE_MS) {
          loggingService.debug(
            "Schedule.ts",
            `🛑 Skipping schedule refresh (source=${source}, called ${now - this.lastRefreshAt}ms after previous run)`
          );
          return { error: null, schedule: null };
        }
      } else {
        loggingService.debug("Schedule.ts", `⚡ Force refresh requested by ${source}, bypassing guard rails`);
      }

      this.refreshInProgress = true;
      loggingService.info("Schedule.ts", `♻️ Fetching schedule (source=${source})`);

      const refreshPromise = httpService.getSchedule().then(({ error, schedule }) => {
        if (error || !schedule) {
          loggingService.error("Schedule.ts", `❗ Failed to fetch schedule (source=${source}): ${error}`);

          if (error?.response?.status === 429) {
            this.rateLimitUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
            loggingService.warn(
              "Schedule.ts",
              `🚦 Rate limit hit (source=${source}), backing off for ${RATE_LIMIT_BACKOFF_MS / 1000}s before next fetch`
            );
          }

          return { error, schedule };
        }

        const { reservations } = convertFromDTO(schedule);

        this.applyReservationsSnapshot(reservations);
        const refreshDuration = (this.lastRefreshAt ?? Date.now()) - now;
        loggingService.info(
          "Schedule.ts",
          `✅ Schedule refreshed with ${reservations.length} reservations (source=${source}, took ${refreshDuration}ms)`
        );

        return { error, schedule };
      });

      ongoingRefresh = refreshPromise.finally(() => {
        this.lastRefreshAt = Date.now();
        this.refreshInProgress = false;
        ongoingRefresh = null;
      });

      return ongoingRefresh;
    },
    async useNowResource() {
      // Unlike the schedule fetch, this one cannot omit the zone: it is stamped onto the created
      // reservation, and `Reservation` validates `time_zone` for presence and identity.
      const tz = deviceTimeZone() ?? "UTC";
      const response = await httpService.useNowResource(tz);

      if (response.error) {
        await this.forceRefreshAfterMutationValidationError("schedule:use-now", response.error);
        return response;
      }

      const applied = this.applyMutationPayload(response.data ?? null, "schedule:use-now");

      if (!applied) {
        loggingService.info(
          "Schedule.ts",
          "⚠️ Mutation response for use now missing schedule data, falling back to schedule refresh"
        );
        const fallback = await this.refreshTablet({ source: "schedule:use-now:fallback", force: true });

        if (fallback.error) {
          return { ...response, error: fallback.error };
        }
      }

      return response;
    },
    async endInProgressReservation() {
      const reservation = this.firstFutureReservation;

      if (this.getScreenMode !== MODE.INPROGRESS) {
        loggingService.warn("Schedule.ts", "Cannot end reservation which is not in progress");
        return { error: "" };
      }

      const response = await httpService.endReservation(reservation.id);

      if (response.error) {
        await this.forceRefreshAfterMutationValidationError("schedule:end-now", response.error);
        return response;
      }

      const applied = this.applyMutationPayload(response.data ?? null, "schedule:end-now");
      if (!applied) {
        this.reservations = this.reservations.filter(e => e.id !== reservation.id);
      }

      return response;
    },
    async checkIn() {
      const reservation = this.firstFutureReservation;

      if (this.getScreenMode !== MODE.COMINGUP) {
        loggingService.warn("Schedule.ts", "Cannot checkin reservation which is not coming up");
        return { error: null, data: null };
      }

      const response = await httpService.checkIn(reservation.id);

      if (response.error) {
        await this.forceRefreshAfterMutationValidationError("schedule:check-in", response.error);
        return response;
      }

      this.applyMutationPayload(response.data ?? null, "schedule:check-in");

      return response;
    },
    async addTime(minutes: number) {
      const reservation = this.firstFutureReservation;

      if (this.getScreenMode !== MODE.INPROGRESS) {
        loggingService.warn("Schedule.ts", "Cannot add time to reservation which has not begun");
        return { error: "" };
      }

      const response = await httpService.addTime(reservation.id, minutes);

      if (response.error) {
        await this.forceRefreshAfterMutationValidationError("schedule:add-time", response.error);
        return response;
      }

      const applied = this.applyMutationPayload(response.data ?? null, "schedule:add-time");

      if (!applied) {
        loggingService.info(
          "Schedule.ts",
          "⚠️ Mutation response for add time missing schedule data, falling back to schedule refresh"
        );
        const fallback = await this.refreshTablet({ source: "schedule:add-time:fallback", force: true });

        if (fallback.error) {
          return { ...response, error: fallback.error };
        }
      }

      return response;
    },
  },
  getters: {
    getTime: state => {
      return state.time;
    },
    getReservations: state => {
      return state.reservations;
    },
    futureReservations(): Reservation[] {
      return getReservationsFinishingInFuture(this.reservations, this.getTime.getTime());
    },
    firstFutureReservation(): Reservation {
      return this.futureReservations[0];
    },
    upcomingReservations(): Reservation[] {
      return this.futureReservations.filter(reservation => {
        if (reservation.startTime > this.getTime) {
          if (reservationStatus.isComingUp(reservation)) {
            if (this.getScreenMode === MODE.INPROGRESS) {
              return true;
            }

            if (this.getScreenMode === MODE.COMINGUP) {
              return false;
            }
          }

          return true;
        } else {
          return false;
        }
      });
    },
    nearestUpcomingReservation(): Reservation {
      return this.upcomingReservations[0];
    },
    getScreenMode() {
      const firstEvent = this.firstFutureReservation;
      const profileStore = useProfileStore();
      const now = this.getTime;

      const isAvailable = profileStore.isAvailable(now);

      if (!isAvailable && !firstEvent) return MODE.UNAVAILABLE;

      if (isAvailable && !firstEvent) return MODE.AVAILABLE;

      if (!profileStore.checkInRequired) {
        if (reservationStatus.isInProgress(firstEvent, now)) {
          return MODE.INPROGRESS;
        }

        if (reservationStatus.isComingUp(firstEvent, now)) {
          return MODE.COMINGUP;
        }
      } else {
        if (
          (firstEvent.createdVia === CreatedVia.TABLET ||
            firstEvent.createdVia === CreatedVia.ADHOC ||
            firstEvent.checkedIn) &&
          reservationStatus.isInProgress(firstEvent, now)
        ) {
          return MODE.INPROGRESS;
        } else if (reservationStatus.isComingUp(firstEvent, now) || reservationStatus.isPostComingUp(firstEvent, now)) {
          return MODE.COMINGUP;
        }
      }

      if (!isAvailable) {
        return MODE.UNAVAILABLE;
      }

      return MODE.AVAILABLE;
    },
  },
});
